import { HttpErrorResponse } from '@angular/common/http';
import { type NativeDragSource, validateNativeDragBatch } from '@sharedlib/common/boards/native-drag-payload';
import { type Observable } from 'rxjs';
import { BoardConversionClient, type ConversionResolution, type ConversionUnitView, type ConversionView, isLostAnswer } from './board-conversion.client';
import { type CardPlacement, type CardSource, type SourceContent } from './drag-create-content';

export interface DragCreateDeps {
  readonly client: {
    convert(request: Parameters<BoardConversionClient['convert']>[0]): Observable<ConversionView>;
    status(commandUid: string): Observable<ConversionView>;
    finalize(commandUid: string, resolution: ConversionResolution): Observable<ConversionView>;
    cardPlaced(commandUid: string, boardUid: string): Observable<ConversionView>;
  };
  /** Places the source card (and text) on one new Board; the command uid is stable per unit across retries. May reject (runtime or read failure). */
  readonly placeCard: (boardUid: string, source: CardSource, commandUid: string) => Promise<CardPlacement>;
  /** False once the space changed or the host was destroyed: nothing more is started or reported. */
  readonly isCurrent: () => boolean;
  readonly destinationAvailable: (folderUid: string | null) => boolean;
  readonly newCommandUid: () => string;
  /** Waits between reads while a cancellation settles. */
  readonly wait: (ms: number) => Promise<void>;
  /** Adds the created Board to the list the person is looking at. May reject. */
  readonly accept: (boardUid: string) => Promise<void>;
  /**
   * A copy needs the source Board's CURRENT scene on the server: its open editor saves first, and a failed save vetoes the
   * copy (native `duplicateDeck`; checklist K "Flush before copy"). Resolves true when every board is safe on the server.
   */
  readonly flushBoards: (boardUids: readonly string[]) => Promise<boolean>;
  /** Loads one source through the native services (as `PanelDeckCreateComponent` does) and extracts its current content. May reject. */
  readonly loadSource: (itemType: string, id: string) => Promise<SourceContent>;
}

/** The drag summary: only its IDENTITY, tenant and lifecycle are read (and validated); its text is never content. */
export interface DragEntry {
  readonly itemType: string;
  readonly id: string;
  readonly accountId: string;
  readonly title: string;
  readonly deleted: boolean;
  readonly recurrenceParentId?: string;
}

export interface DragCreateOutcome {
  readonly status: 'done' | 'partial' | 'refused' | 'stale' | 'cancelled';
  /** Boards that exist now, in drop order. */
  readonly boardUids: readonly string[];
  /** What to tell the person (never a title or a stack). */
  readonly message: string;
  /** Members left out because they were gone or in Trash; a host shows the message even on success when set. */
  readonly skipped?: number;
}

interface Pending {
  readonly commandUid: string;
  readonly entries: readonly NativeDragSource[];
  readonly contents: ReadonlyMap<string, SourceContent>;
  readonly folder: string | null;
  readonly cardUids: Map<string, string>;
  readonly accepted: Set<string>;
  /** Members left out because they were gone or in Trash; the outcome names them. */
  readonly skipped: number;
  /** A request of this command went unanswered: the server may still be running it, so no new command may ever replace it. */
  unanswered?: boolean;
  /** The last answer of the receipt; absent until the server has answered once (the receipt may or may not exist). */
  view?: ConversionView;
}

/** After a cancel that is not terminal yet: how many times, and how far apart, the server is asked before nothing is announced. */
export const CANCEL_SETTLE_READS = 5;
export const CANCEL_SETTLE_MS = 400;

/** Final: nothing was created or deleted by us. A new conversion is a new explicit intent. */
export const UNKNOWN_MESSAGE = 'The outcome of a creation could not be confirmed. Open Boards, Slides or Sheets to check; start a new conversion if you still want it.';

const keyOf = (itemType: string, id: string): string => `${itemType}:${id}`;

const UNAVAILABLE = 'A source is unavailable or in Trash.';

/** The server refused a source at admission (gone or in Trash since the drop): nothing was recorded for the command. */
const refusedSource = (error: unknown): boolean => error instanceof HttpErrorResponse && error.status === 404 && !isLostAnswer(error);

const withSkipped = (message: string, skipped: number): string =>
  skipped ? `${message} ${skipped === 1 ? 'One source was' : `${skipped} sources were`} unavailable or in Trash and left out.` : message;

/**
 * Drag to create (PAR-14), the Boards counterpart of `PanelDeckCreateComponent.drop/resumeDrop` (panel-deck-create.component.ts:175-270):
 * a distinct creation flow from "Create from this item". The destination folder is captured at the drop; the dragged members are
 * validated first (a foreign, unsupported or trashed member refuses the whole drop); every source is loaded through the native
 * services, so the Board's content comes from the authorized CURRENT item, never from the drag summary. A source that cannot be
 * loaded, or is in Trash by then, is left out and named while the others still get their Boards, as Slides still makes the decks
 * of the members it reaches (R13 T-B row 5). Then ONE durable conversion command (one uid for the drop) creates one Board per
 * intended source and its Stitch; then each new Board gets its card.
 *
 * Every failure after the first request is a RECOVERABLE partial outcome that keeps the command uid and the targets (placement,
 * runtime-load and read failures included): `retry()` resumes the same receipt (the finalizer's `retry` resolution, which also
 * reopens a refused link); `keepWithoutLink()` records the person's choice for a refused link; `cancel()` is persisted by the server
 * (`cancel_remaining`) and is reported only once the server has acknowledged it, so an unknown answer never reads as cancelled.
 */
export class DragCreateFlow {
  private pending: Pending | undefined;
  private busy = false;

  constructor(private readonly deps: DragCreateDeps, private readonly spaceId: () => string) {}

  public get canRetry(): boolean {
    return !!this.pending;
  }

  public get isBusy(): boolean {
    return this.busy;
  }

  /** A source refused its link: the explicit "keep without the link" decision is on offer. */
  public get linkRefused(): boolean {
    return !!this.pending?.view?.units.some((unit) => unit.target === 'done' && unit.relation === 'refused');
  }

  public async drop(entries: readonly DragEntry[], folder: string | null): Promise<DragCreateOutcome> {
    if (this.busy || this.pending) return { status: 'refused', boardUids: [], message: 'Finish or cancel the current creation first.' };
    const verdict = validateNativeDragBatch(entries, { purpose: 'create', spaceId: this.spaceId() });
    if (!('sources' in verdict)) return { status: 'refused', boardUids: [], message: 'Drop saved items from your own space to create from them.' };
    if (!this.deps.destinationAvailable(folder)) return { status: 'refused', boardUids: [], message: 'This folder is no longer available.' };
    this.busy = true;
    try {
      const boards = verdict.sources.filter((source) => source.itemType === 'board').map((source) => source.id);
      if (boards.length && !(await this.deps.flushBoards(boards))) {
        return { status: 'refused', boardUids: [], message: 'A source board could not be saved first, so no copy was made.' };
      }
      // Authorized current content, loaded through the native services BEFORE anything is created (native resumeDrop); a source
      // that cannot be loaded or is in Trash is left out, and only when none is left does the drop create nothing.
      const contents = new Map<string, SourceContent>();
      const gone = new Set<string>();
      for (const source of verdict.sources.filter((item) => item.itemType !== 'board')) {
        const content = await this.deps.loadSource(source.itemType, source.id).catch(() => null);
        if (!content || content.deleted) gone.add(keyOf(source.itemType, source.id));
        else contents.set(keyOf(source.itemType, source.id), content);
      }
      const sources = verdict.sources.filter((source) => !gone.has(keyOf(source.itemType, source.id)));
      if (!sources.length) return { status: 'refused', boardUids: [], message: UNAVAILABLE };
      if (!this.deps.isCurrent()) return { status: 'stale', boardUids: [], message: '' };
      this.pending = { commandUid: this.deps.newCommandUid(), entries: sources, contents, folder, cardUids: new Map(), accepted: new Set(), skipped: gone.size };
    } finally {
      this.busy = false;
    }
    return this.run('convert');
  }

  /** Asks the same command again; the finalizer's `retry` also reopens a refused link. Finishes only what is unfinished. */
  public async retry(): Promise<DragCreateOutcome> {
    if (this.busy || !this.pending) return { status: 'refused', boardUids: [], message: 'Nothing to retry.' };
    return this.run(this.pending.view ? 'retry' : 'convert');
  }

  /** The explicit decision on a refused link: keep the Board without it (the receipt records it). */
  public async keepWithoutLink(): Promise<DragCreateOutcome> {
    if (this.busy || !this.pending) return { status: 'refused', boardUids: [], message: 'Nothing to decide.' };
    return this.run('keep');
  }

  /**
   * Cancel remaining items. The rule is simple: only units whose creation never started are cancelled. Every unit already
   * attempted, including one whose outcome is unknown, settles through the server's own receipt (a committed target is kept, a
   * refusal is reported, a failure keeps Retry). "Cancelled" is announced only once the receipt says every attempted unit has
   * settled, so a cancel that is not yet COMMITTED is read again for a short while and otherwise announces nothing; an unknown
   * answer keeps Retry and Cancel on offer. A missing receipt proves nothing: the server reserves the cancellation itself, so a
   * late first request cannot create anything.
   */
  public async cancel(): Promise<DragCreateOutcome> {
    const pending = this.pending;
    if (this.busy || !pending) return { status: 'refused', boardUids: [], message: 'Nothing to cancel.' };
    this.busy = true;
    try {
      let view: ConversionView;
      try {
        view = await BoardConversionClient.replayingLostAnswer(() => this.deps.client.finalize(pending.commandUid, 'cancel_remaining'));
        for (let attempt = 0; view.status !== 'COMMITTED' && view.units.some((unit) => unit.target === 'pending') && attempt < CANCEL_SETTLE_READS; attempt++) {
          await this.deps.wait(CANCEL_SETTLE_MS);
          view = await BoardConversionClient.replayingLostAnswer(() => this.deps.client.status(pending.commandUid));
        }
      } catch {
        return { status: 'partial', boardUids: boardsOf(pending.view), message: 'Cancelling could not be confirmed. Nothing was cancelled yet: try again, or retry to finish.' };
      }
      pending.view = view;
      return await this.concludeCancelled(view);
    } finally {
      this.busy = false;
    }
  }

  private async run(mode: 'convert' | 'retry' | 'keep'): Promise<DragCreateOutcome> {
    const pending = this.pending!;
    this.busy = true;
    try {
      let view: ConversionView;
      let attempts = 0;
      try {
        view = await BoardConversionClient.replayingLostAnswer(() => {
          attempts++;
          return mode === 'convert' ? this.deps.client.convert(this.request(pending)) : this.deps.client.finalize(pending.commandUid, mode === 'retry' ? 'retry' : 'accept_without_link');
        });
      } catch (error) {
        // A replay means the first request went unanswered; that uncertainty stays with the command through every later retry.
        if (attempts > 1 || isLostAnswer(error)) pending.unanswered = true;
        if (mode === 'convert' && refusedSource(error)) return await this.withoutUnavailable(pending);
        return { status: 'partial', boardUids: boardsOf(pending.view), message: 'Could not create from these items. Retry to continue.' };
      }
      pending.view = view;
      if (!this.deps.isCurrent()) return { status: 'stale', boardUids: boardsOf(view), message: '' };
      // A cancelled receipt (a retry or a replay after a cancel) is never shown as a fresh creation: no cards, no success message.
      if (view.cancelled) return await this.concludeCancelled(view);
      let trouble = false;
      for (const unit of view.units) {
        if (unit.target === 'done' && unit.boardUid && unit.card === 'pending' && !(await this.placeOne(view, unit, pending))) trouble = true;
      }
      if (!this.deps.isCurrent()) return { status: 'stale', boardUids: boardsOf(view), message: '' };
      // The persisted phases after the cards (a read: asking `convert` again would need the identical body).
      try {
        view = await BoardConversionClient.replayingLostAnswer(() => this.deps.client.status(pending.commandUid));
        pending.view = view;
      } catch {
        trouble = true;
      }
      if (view.cancelled) return await this.concludeCancelled(view);
      const boardUids = boardsOf(view);
      for (const uid of boardUids) {
        if (pending.accepted.has(uid)) continue;
        try {
          await this.deps.accept(uid);
          pending.accepted.add(uid);
        } catch {
          trouble = true;
        }
      }
      // The last known terminal outcome survives a failed refresh. Recovery stays only while a KNOWN board still has work to finish.
      if (view.status === 'COMMITTED' && view.outcomeUnknown) {
        const unfinished = trouble && boardUids.length > 0;
        if (!unfinished) this.pending = undefined;
        return { status: 'partial', boardUids, message: unfinished ? `Some boards could not be shown or filled yet. Retry to finish. ${UNKNOWN_MESSAGE}` : UNKNOWN_MESSAGE };
      }
      if (view.status === 'COMMITTED' && !trouble) {
        this.pending = undefined;
        const made = boardUids.length === 1 ? 'New editable board created and stitched to its source.' : `${boardUids.length} editable boards created and stitched to their sources.`;
        return { status: 'done', boardUids, message: withSkipped(made, pending.skipped), ...(pending.skipped ? { skipped: pending.skipped } : {}) };
      }
      return {
        status: 'partial',
        boardUids,
        message: withSkipped(trouble && view.status === 'COMMITTED' ? 'The boards were made, but some could not be shown or filled yet. Retry to finish.' : pendingMessage(view), pending.skipped),
      };
    } catch {
      // Anything unexpected after the command exists still leaves it recoverable: same command, same targets.
      return { status: 'partial', boardUids: boardsOf(pending.view), message: 'Something went wrong before this finished. Retry to continue.' };
    } finally {
      this.busy = false;
    }
  }

  /**
   * The server refused a source at admission (it went to Trash or away after the drop), so nothing was recorded for THIS request
   * and asking the same command again could never succeed. The members are read again: the readable ones go out as a NEW command
   * (its body differs) and the gone ones are named. With nothing readable left, or nothing found gone, the flow ends with the cause.
   * After an unanswered request of the command, a refusal proves nothing about that earlier request, which may still create the
   * Boards: recovery stays under the original command (Retry asks it again; Cancel is reserved by the server and fences it).
   */
  private async withoutUnavailable(pending: Pending): Promise<DragCreateOutcome> {
    if (pending.unanswered) {
      return { status: 'partial', boardUids: [], message: `${UNAVAILABLE} An earlier request is not confirmed yet: retry to check it, or cancel and drag the other items again.` };
    }
    const gone = new Set<string>();
    for (const source of pending.entries.filter((item) => item.itemType !== 'board')) {
      const content = await this.deps.loadSource(source.itemType, source.id).catch(() => null);
      if (!content || content.deleted) gone.add(keyOf(source.itemType, source.id));
    }
    const entries = pending.entries.filter((source) => !gone.has(keyOf(source.itemType, source.id)));
    if (!gone.size || !entries.length) {
      this.pending = undefined;
      return { status: 'refused', boardUids: [], message: gone.size ? UNAVAILABLE : 'Could not create from these items.' };
    }
    if (!this.deps.isCurrent()) {
      this.pending = undefined;
      return { status: 'stale', boardUids: [], message: '' };
    }
    this.pending = { ...pending, commandUid: this.deps.newCommandUid(), entries, skipped: pending.skipped + gone.size, view: undefined };
    return this.run('convert');
  }

  /**
   * The one place a cancelled receipt becomes an outcome. COMMITTED means every attempted unit has settled: the flow ends with
   * the retained targets and the never-created units listed. Anything else (a unit still settling or refused) is partial, keeps
   * the flow, and says so; it is never a success message.
   */
  private async concludeCancelled(view: ConversionView): Promise<DragCreateOutcome> {
    const kept = view.units.filter((unit) => unit.target === 'done' && unit.boardUid).map((unit) => unit.boardUid as string);
    const notCreated = view.units.filter((unit) => unit.target === 'cancelled').length;
    const failed = view.units.filter((unit) => unit.target === 'failed').length;
    if (view.status !== 'COMMITTED') {
      return {
        status: 'partial',
        boardUids: kept,
        message: failed
          ? `Cancelling: ${failed} board${failed === 1 ? '' : 's'} could not be created. Retry to settle ${failed === 1 ? 'it' : 'them'}. ${kept.length} already made ${kept.length === 1 ? 'was' : 'were'} kept.`
          : 'Cancelling is waiting for work in progress to settle. Try again in a moment.',
      };
    }
    this.pending = undefined;
    for (const uid of kept) await this.deps.accept(uid).catch(() => undefined);
    const unknown = view.units.filter((unit) => unit.target === 'unknown').length;
    return {
      status: 'cancelled',
      boardUids: kept,
      message: `Cancelled. ${kept.length} board${kept.length === 1 ? '' : 's'} kept${notCreated ? `; ${notCreated} never created` : ''}.${unknown ? ` ${UNKNOWN_MESSAGE}` : ''}`,
    };
  }

  private request(pending: Pending): Parameters<BoardConversionClient['convert']>[0] {
    return {
      commandUid: pending.commandUid,
      recipe: 'drag_to_create_board',
      sources: pending.entries.map((entry) => {
        const description = pending.contents.get(keyOf(entry.itemType, entry.id))?.descriptionHtml;
        return { itemType: entry.itemType, id: entry.id, ...(description ? { description } : {}) };
      }),
      folderUid: pending.folder,
    };
  }

  /** Places one card; any failure, including a rejected runtime load, is `false` (recoverable), never an escape. */
  private async placeOne(view: ConversionView, unit: ConversionUnitView, pending: Pending): Promise<boolean> {
    const boardUid = unit.boardUid as string;
    const key = keyOf(unit.source.itemType, unit.source.id);
    let cardUid = pending.cardUids.get(key);
    if (!cardUid) pending.cardUids.set(key, (cardUid = this.deps.newCommandUid()));
    const content = pending.contents.get(key);
    let placed: CardPlacement;
    try {
      placed = await this.deps.placeCard(boardUid, { itemType: unit.source.itemType as never, id: unit.source.id, title: content?.title || unit.title, text: content?.text }, cardUid);
    } catch {
      return false;
    }
    if (placed === 'failed') return false;
    try {
      await BoardConversionClient.replayingLostAnswer(() => this.deps.client.cardPlaced(view.commandUid, boardUid));
    } catch {
      // The card is on the canvas; the receipt learns it on the next retry (placeCard answers 'already').
      return false;
    }
    return true;
  }
}

const boardsOf = (view: ConversionView | undefined): string[] => (view ? view.units.flatMap((unit) => (unit.boardUid ? [unit.boardUid] : [])) : []);

function pendingMessage(view: ConversionView): string {
  const base = pendingBase(view);
  return view.outcomeUnknown ? `${base} ${UNKNOWN_MESSAGE}` : base;
}

function pendingBase(view: ConversionView): string {
  if (view.pending.includes('relation_refused')) return 'A source refused its link. The board was kept; retry the link or keep it without the link.';
  if (view.pending.includes('target')) return 'Some boards could not be created yet. Retry to finish.';
  if (view.pending.includes('relation')) return 'Some boards were made, but a source link is pending. Retry the link.';
  return 'Some boards are missing their content. Retry to finish.';
}
